/**
 * Outil « Urne et tirages » (Boîte à outils, 4e → prépa).
 *
 * On règle l'urne (rouges, bleues, vertes), 2 ou 3 tirages, avec ou sans
 * remise. On tire boule par boule (sans remise, la boule sortie laisse un
 * fantôme ; un cercle suit le chemin dans l'arbre) et l'arbre pondéré se
 * construit tout seul, en fractions EXACTES sur un dénominateur commun :
 * sans remise, on voit 5/8 puis 4/7. Un événement (« même couleur », « au
 * moins une rouge »…) surligne ses chemins ; P(E) = somme des chemins =
 * favorables / possibles, envoyable dans la feuille. Simulation.
 * Selon le niveau : 1re probabilités conditionnelles comparées à P(B₂)
 * (indépendance), loi de X lue sur l'arbre et E(X) par la définition ; Tle
 * dénombrement (k-uplets), binomiale avec remise, combinaisons sans remise,
 * E(X) = k·r/N ; prépa A_N^k, loi hypergéométrique, variance.
 * Calculs : `urn.ts`.
 */

import { useState, type ReactNode } from 'react';
import { Minus, Plus } from 'lucide-react';
import { schoolLevelRank } from '@jeraisonne/db/cards';
import { MathText, cn } from '@jeraisonne/ui';
import { SendToSheetButton } from '../../send-to-sheet-button';
import type { ToolProps } from '../../tool-types';
import { Note, PRIMARY_BUTTON, SECONDARY_BUTTON, Segmented } from '../../ui/controls';
import { formatNumber } from '../../ui/format';
import { FIGURE_BG_CLASS, HaloText, MUTED } from '../../ui/svg-parts';
import { fraction } from '../random-launcher/launcher';
import {
  allPaths,
  COLORS,
  countLaw,
  denominator,
  drawOne,
  eventById,
  eventProbability,
  EVENTS,
  expectation,
  feasible,
  lawFromPaths,
  MAX_PER_COLOR,
  pathName,
  remaining,
  simulate,
  total,
  variance,
  type EventId,
  type UrnPath,
  type UrnSetup,
} from './urn';

type Mode = 'college' | 'seconde' | 'premiere' | 'terminale' | 'prepa';

function modeOf(rank: number | null): Mode {
  if (rank === null) return 'terminale';
  if (rank <= 4) return 'college';
  if (rank === 5) return 'seconde';
  if (rank === 6) return 'premiere';
  return rank === 7 ? 'terminale' : 'prepa';
}

/** au-delà, l'arbre ne tient plus sur un téléphone : on liste les chemins */
const MAX_TREE_LEAVES = 12;
const SIM_BATCH = 1000;
const SIM_MAX = 100000;
const ORDINAL = ['1re', '2e', '3e'];
const HIGHLIGHT = 'var(--color-primary-500)';
const HIGHLIGHT_TEXT = 'var(--color-primary-600)';
const DRAWN = 'var(--color-accent-700)';
const DISABLED = 'disabled:cursor-not-allowed disabled:opacity-40';

/** 100000 → « 100 000 » (espace fine insécable U+202F) */
const count = (n: number) => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
const frac = (n: number, d: number) => `\\frac{${n}}{${d}}`;
/** « rouge puis bleue » : un chemin dit à voix haute */
const spoken = (colors: readonly number[]) => colors.map((x) => COLORS[x]!.name).join(' puis ');

export function UrnDrawsTool({
  onSend,
  sendCost,
  level,
  rng = Math.random,
}: ToolProps & { level?: string | null; rng?: () => number }) {
  const mode = modeOf(schoolLevelRank(level));
  const lycee = mode === 'premiere' || mode === 'terminale' || mode === 'prepa';
  const [counts, setCounts] = useState<number[]>([5, 3, 0]);
  const [k, setK] = useState(2);
  const [replace, setReplace] = useState(false);
  const [eventId, setEventId] = useState<EventId>('same');
  const [c, setC] = useState(0);
  const [drawn, setDrawn] = useState<number[]>([]);
  const [sim, setSim] = useState<{ n: number; hits: number } | null>(null);

  const setup: UrnSetup = { counts, k, replace };
  const n = total(counts);
  const ok = feasible(setup);
  const paths = allPaths(setup);
  const D = denominator(setup);
  const ev = eventById(eventId);
  const res = eventProbability(setup, eventId, c);
  const p = fraction(res.num, res.den || 1);
  const highlighted = new Set(res.paths.map((q) => pathName(q.colors)));
  const done = drawn.length === k;
  const colorName = COLORS[c]!.name;
  const eventLabel = ev.label(colorName, k);
  const colorsPresent = counts.filter((x) => x > 0).length;
  const showTree = paths.length <= MAX_TREE_LEAVES;

  /** tout changement de l'expérience efface le tirage en cours et la simulation */
  const changeSetup = (fn: () => void) => {
    fn();
    setDrawn([]);
    setSim(null);
  };
  const setCount = (i: number, v: number) =>
    changeSetup(() => setCounts((old) => old.map((x, j) => (j === i ? v : x))));

  // garde dans l'updater : deux taps rapides ne tirent jamais plus de k boules
  const drawNext = () => {
    if (!ok) return;
    setDrawn((old) => (old.length >= k ? old : [...old, drawOne(counts, old, replace, rng)]));
  };
  const runSim = () =>
    setSim((old) => {
      const prev = old ?? { n: 0, hits: 0 };
      if (prev.n >= SIM_MAX) return prev;
      return {
        n: prev.n + SIM_BATCH,
        hits: prev.hits + simulate(setup, eventId, c, SIM_BATCH, rng),
      };
    });

  const left = remaining(counts, drawn, replace);
  const last = drawn[drawn.length - 1];
  const status =
    drawn.length === 0
      ? `Urne : ${describe(counts)}.`
      : done
        ? `Tirage ${pathName(drawn)} (${spoken(drawn)}) : l’événement « ${eventLabel} » ${ev.test(drawn, c) ? 'est réalisé' : 'n’est pas réalisé'}.`
        : `${ORDINAL[drawn.length - 1]} boule : ${COLORS[last!]!.name}${
            replace
              ? ', remise dans l’urne.'
              : `. Il reste ${total(left)} boule${total(left) > 1 ? 's' : ''} : ${describe(left)}.`
          }`;

  return (
    <div className="space-y-4">
      <section
        className="border-ink-100 grid grid-cols-3 gap-1 rounded-2xl border p-2"
        aria-label="Composition de l’urne"
      >
        {COLORS.map((col, i) => (
          <div key={col.id} className="flex flex-col items-center gap-1">
            <span className="text-ink-900 flex items-center gap-1.5 text-sm font-semibold">
              <Ball color={i} size="sm" />
              <span className="tabular-nums">{counts[i]}</span>
              <span className="text-ink-600 font-normal">
                {counts[i]! > 1 ? col.plural : col.name}
              </span>
            </span>
            <div className="flex">
              <CountButton
                label={`Boules ${col.plural} : moins`}
                disabled={counts[i] === 0 || n === 1}
                onClick={() => setCount(i, counts[i]! - 1)}
              >
                <Minus className="h-5 w-5" aria-hidden />
              </CountButton>
              <CountButton
                label={`Boules ${col.plural} : plus`}
                disabled={counts[i] === MAX_PER_COLOR}
                onClick={() => setCount(i, counts[i]! + 1)}
              >
                <Plus className="h-5 w-5" aria-hidden />
              </CountButton>
            </div>
          </div>
        ))}
      </section>

      <div className="flex flex-wrap gap-2">
        <Segmented
          ariaLabel="Remise"
          value={replace ? 'avec' : 'sans'}
          options={[
            { value: 'sans', label: 'Sans remise' },
            { value: 'avec', label: 'Avec remise' },
          ]}
          onChange={(v) => changeSetup(() => setReplace(v === 'avec'))}
        />
        <Segmented
          ariaLabel="Nombre de tirages"
          value={String(k)}
          options={[
            { value: '2', label: '2 tirages' },
            { value: '3', label: '3 tirages' },
          ]}
          onChange={(v) => changeSetup(() => setK(Number(v)))}
        />
      </div>

      <UrnFigure counts={counts} drawn={drawn} replace={replace} k={k} />
      <div className="flex flex-wrap items-center gap-2">
        {/* le bouton principal reste en place (désactivé à la fin) : un double tap
            sur le dernier tirage ne peut pas l'effacer */}
        <button
          type="button"
          className={cn(PRIMARY_BUTTON, DISABLED)}
          disabled={!ok || done}
          onClick={drawNext}
        >
          {done
            ? 'Tirage terminé'
            : drawn.length === 0
              ? 'Tirer une boule'
              : `Tirer la ${ORDINAL[drawn.length]}`}
        </button>
        {drawn.length > 0 && (
          <button type="button" className={SECONDARY_BUTTON} onClick={() => setDrawn([])}>
            {done ? 'Nouveau tirage' : 'Recommencer'}
          </button>
        )}
      </div>
      <p className="text-ink-700 text-sm" aria-live="polite" data-testid="urn-status">
        {ok ? status : `Sans remise, il faut au moins ${k} boules dans l’urne pour ${k} tirages.`}
      </p>

      {ok && (
        <>
          <Segmented
            ariaLabel="Événement"
            value={eventId}
            options={EVENTS.map((e) => ({ value: e.id, label: e.label(colorName, k) }))}
            onChange={(v) => {
              setEventId(v);
              setSim(null);
            }}
          />
          {(ev.usesColor || lycee) && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-ink-600 text-sm">Couleur suivie</span>
              <Segmented
                ariaLabel="Couleur suivie"
                value={String(c)}
                options={COLORS.map((col, i) => ({ value: String(i), label: capital(col.name) }))}
                onChange={(v) => {
                  setC(Number(v));
                  setSim(null);
                }}
              />
            </div>
          )}

          {showTree ? (
            <TreeFigure paths={paths} D={D} k={k} highlighted={highlighted} drawn={drawn} />
          ) : (
            <section className="space-y-2" data-testid="urn-paths">
              <p className="text-ink-700 text-sm">
                L’arbre aurait {paths.length} chemins : trop grand pour l’écran.{' '}
                {res.paths.length === 0
                  ? 'Aucun chemin ne réalise l’événement.'
                  : `Voici les chemins de l’événement (dénominateur commun ${count(D)}) :`}
              </p>
              {res.paths.length > 0 && (
                <ul className="flex flex-wrap gap-1">
                  {res.paths.map((q) => {
                    const name = pathName(q.colors);
                    const mine = done && name === pathName(drawn);
                    return (
                      <li
                        key={name}
                        className={cn(
                          'text-ink-800 rounded-lg border px-2 py-0.5 text-sm tabular-nums',
                          mine ? 'border-accent-700 border-2 font-semibold' : 'border-ink-200',
                        )}
                        aria-label={`${spoken(q.colors)} : ${q.num} sur ${D}${mine ? ', ton tirage' : ''}`}
                      >
                        {name} · {q.num}/{count(D)}
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          )}

          <section
            className="border-ink-100 space-y-3 rounded-2xl border px-3 py-3"
            aria-label="Probabilité de l’événement"
            data-testid="urn-results"
          >
            <p className="text-ink-900 text-sm font-semibold">E : « {eventLabel} »</p>
            {res.paths.length > 0 && res.paths.length <= 4 && (
              <div className="space-y-0.5">
                {res.paths.map((q) => (
                  <MathText key={pathName(q.colors)} className="text-ink-700 block overflow-x-auto">
                    {`$\\text{${pathName(q.colors)}} : ${q.branches.map((b) => frac(b.num, b.den)).join(' \\times ')} = ${frac(q.num, D)}$`}
                  </MathText>
                ))}
              </div>
            )}
            <MathText className="text-ink-900 block overflow-x-auto text-lg">
              {`$${probabilityTex(res.paths, res.num, D, p.tex)}$`}
            </MathText>
            {onSend && (
              <div className="flex justify-end">
                <SendToSheetButton
                  label={p.plain}
                  cost={sendCost}
                  onClick={() => onSend({ atoms: p.atoms })}
                />
              </div>
            )}
            <p className="text-ink-600 text-sm">
              {res.paths.length === 0
                ? 'Aucun chemin ne réalise E : c’est un événement impossible.'
                : res.num === D
                  ? 'Tous les chemins réalisent E : c’est un événement certain.'
                  : mode === 'college'
                    ? 'On multiplie le long d’un chemin, puis on ajoute les chemins qui réalisent E.'
                    : res.paths.length === 1
                      ? `Un seul chemin réalise E${showTree ? ' (en gras sur l’arbre)' : ''}.`
                      : `On ajoute les ${res.paths.length} chemins qui réalisent E${showTree ? ' (en gras sur l’arbre)' : ''}.`}
            </p>
            {(mode === 'college' || mode === 'seconde') && res.num > 0 && res.num < D && (
              <p className="text-ink-600 text-sm" data-testid="urn-count">
                Autre lecture : en numérotant les boules, il y a{' '}
                {Array.from({ length: k }, (_, i) => (replace ? n : n - i)).join(' × ')} ={' '}
                {count(D)} tirages possibles, et {count(res.num)} réalisent E.
              </p>
            )}
            {eventId === 'atLeast' && res.num > 0 && res.num < D && (
              <div className="space-y-0.5" data-testid="urn-complement">
                <p className="text-ink-600 text-sm">Plus rapide, par l’événement contraire :</p>
                <MathText className="text-ink-700 block overflow-x-auto">
                  {`$${lines(
                    'P(E)',
                    [
                      { op: '=', body: `1 - P(\\text{aucune ${colorName}})` },
                      { op: '=', body: `1 - ${frac(D - res.num, D)} = ${frac(res.num, D)}` },
                    ],
                    D >= 100,
                  )}$`}
                </MathText>
              </div>
            )}
            {eventId === 'second' && !replace && (
              <Note live={false}>
                Même probabilité que pour la 1re boule : {fraction(counts[c]!, n).plain}. Sans
                savoir ce qui est sorti avant, la 2e boule a autant de chances d’être {colorName}{' '}
                que la 1re.
              </Note>
            )}
          </section>

          {colorsPresent > 1 && (
            <Note live={false}>
              {replace
                ? lycee
                  ? 'Avec remise, l’urne est la même à chaque tirage : mêmes fractions à chaque niveau, les tirages sont indépendants.'
                  : 'Avec remise, l’urne est la même à chaque tirage : les fractions ne changent pas.'
                : lycee
                  ? 'Sans remise, l’urne change : les fractions d’un tirage dépendent des boules déjà sorties, les tirages ne sont pas indépendants.'
                  : 'Sans remise, il reste une boule de moins à chaque tirage : les fractions changent.'}
            </Note>
          )}
          {lycee && colorsPresent > 1 && (
            <ConditionalLine paths={paths} counts={counts} prepa={mode === 'prepa'} />
          )}

          {lycee && (
            <LawPanel
              setup={setup}
              c={c}
              favorable={res.num}
              mode={mode}
              onSend={onSend}
              sendCost={sendCost}
            />
          )}

          <section className="space-y-2" aria-label="Simulation" data-testid="urn-sim">
            <button
              type="button"
              className={cn(SECONDARY_BUTTON, DISABLED)}
              disabled={(sim?.n ?? 0) >= SIM_MAX}
              onClick={runSim}
            >
              Simuler {count(SIM_BATCH)} expériences
            </button>
            {sim && (
              <p className="text-ink-700 text-sm" aria-live="polite">
                Sur {count(sim.n)} expériences, E est réalisé {count(sim.hits)} fois : fréquence ≈{' '}
                {formatNumber(sim.hits / sim.n, 3)} ; P(E) ≈ {formatNumber(p.value, 3)}.
                {sim.n >= SIM_MAX && ` (${count(SIM_MAX)} expériences au maximum.)`}
              </p>
            )}
          </section>
        </>
      )}
    </div>
  );
}

/** « 4 rouges, 3 bleues » (couleurs absentes omises) */
function describe(counts: readonly number[]): string {
  const parts = COLORS.flatMap((col, i) =>
    counts[i]! > 0 ? [`${counts[i]} ${counts[i]! > 1 ? col.plural : col.name}`] : [],
  );
  return parts.length ? parts.join(', ') : 'vide';
}

const capital = (s: string) => s[0]!.toUpperCase() + s.slice(1);

/** « a = b » sur une ligne, ou sur deux lignes alignées quand c'est long */
/**
 * « lhs = a = b » sur une ligne, ou une égalité par ligne, alignées sur le
 * signe (fractions gardées en petit : \textstyle). Une ligne « + … » continue
 * la somme de la ligne précédente.
 */
function lines(lhs: string, rows: Array<{ op: '=' | '+'; body: string }>, split: boolean) {
  if (!split) return `${lhs} ${rows.map((r) => `${r.op} ${r.body}`).join(' ')}`;
  const body = rows
    .map((r, i) =>
      r.op === '='
        ? `${i === 0 ? lhs : ''} &= \\textstyle ${r.body}`
        : `&\\phantom{=}\\ \\textstyle + ${r.body}`,
    )
    .join(' \\\\ ');
  return `\\begin{aligned} ${body} \\end{aligned}`;
}

/** P(E) = 20/56 + 6/56 = 26/56 = 13/28 (somme écrite jusqu'à 4 chemins ; 2 lignes si long) */
function probabilityTex(paths: UrnPath[], num: number, D: number, reduced: string): string {
  if (paths.length === 0) return 'P(E) = 0';
  const terms = paths.length > 1 && paths.length <= 4 ? paths.map((q) => frac(q.num, D)) : [];
  const tail = [frac(num, D), ...(reduced !== frac(num, D) ? [reduced] : [])].join(' = ');
  if (!terms.length) return `P(E) = ${tail}`;
  return lines(
    'P(E)',
    [
      { op: '=', body: terms.join(' + ') },
      { op: '=', body: tail },
    ],
    terms.length > 2 || D >= 100,
  );
}

function CountButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="text-ink-700 hover:bg-ink-100 flex min-h-11 min-w-11 items-center justify-center rounded-xl disabled:opacity-40"
    >
      {children}
    </button>
  );
}

function Ball({ color, size = 'md' }: { color: number; size?: 'sm' | 'md' }) {
  const col = COLORS[color]!;
  return (
    <span
      aria-hidden
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-full font-bold',
        size === 'sm' ? 'h-6 w-6 text-xs' : 'h-8 w-8 text-sm',
      )}
      style={{ background: col.fill, color: col.ink }}
    >
      {col.id}
    </span>
  );
}

const URN_W = 320;
const MAX_PER_ROW = 9;
const STEP = 24;

/** L'urne : les boules rangées par couleur ; sans remise, la boule sortie laisse un fantôme. */
function UrnFigure({
  counts,
  drawn,
  replace,
  k,
}: {
  counts: readonly number[];
  drawn: readonly number[];
  replace: boolean;
  k: number;
}) {
  const balls = counts.flatMap((m, color) => Array.from({ length: m }, () => color));
  const out = replace ? [0, 0, 0] : COLORS.map((_, i) => drawn.filter((x) => x === i).length);
  const rows = Math.max(1, Math.ceil(balls.length / MAX_PER_ROW));
  // rangées égales : 10 boules = 5 + 5, pas 9 + 1
  const perRow = Math.max(1, Math.ceil(balls.length / rows));
  const H = rows * STEP + 22;
  const firstOfColor = counts.map((_, i) => counts.slice(0, i).reduce((s, x) => s + x, 0));
  return (
    <div className="mx-auto w-full max-w-[26rem] space-y-2">
      <svg
        viewBox={`0 0 ${URN_W} ${H}`}
        className="block w-full"
        role="img"
        aria-label={`Urne : ${describe(remaining(counts, drawn, replace))}.`}
        data-testid="urn-figure"
      >
        <path
          d={`M 40 4 L 40 ${H - 10} Q 40 ${H - 3} 48 ${H - 3} L ${URN_W - 48} ${H - 3} Q ${URN_W - 40} ${H - 3} ${URN_W - 40} ${H - 10} L ${URN_W - 40} 4`}
          fill="none"
          stroke="var(--color-ink-400)"
          strokeWidth={2.5}
          strokeLinecap="round"
        />
        {balls.map((color, i) => {
          const row = Math.floor(i / perRow);
          const inRow = Math.min(perRow, balls.length - row * perRow);
          const x = URN_W / 2 - ((inRow - 1) * STEP) / 2 + (i % perRow) * STEP;
          const y = H - 15 - row * STEP;
          const col = COLORS[color]!;
          // sans remise : les dernières boules de la couleur sont celles qui sont sorties
          const gone = i - firstOfColor[color]! >= counts[color]! - out[color]!;
          return gone ? (
            <circle
              key={i}
              cx={x}
              cy={y}
              r={9.5}
              fill="none"
              stroke={col.fill}
              strokeWidth={1.5}
              strokeDasharray="3 3"
              opacity={0.7}
            />
          ) : (
            <g key={i}>
              <circle cx={x} cy={y} r={10} fill={col.fill} />
              <text
                x={x}
                y={y + 4}
                textAnchor="middle"
                fontSize={12}
                fontWeight={700}
                fill={col.ink}
              >
                {col.id}
              </text>
            </g>
          );
        })}
      </svg>
      <div className="flex items-center justify-center gap-3" aria-hidden>
        {Array.from({ length: k }, (_, i) => (
          <span key={i} className="flex flex-col items-center gap-0.5">
            {drawn[i] !== undefined ? (
              <Ball color={drawn[i]!} />
            ) : (
              <span className="border-ink-300 h-8 w-8 rounded-full border-2 border-dashed" />
            )}
            <span className="text-ink-500 text-xs">{ORDINAL[i]}</span>
          </span>
        ))}
        {replace && drawn.length > 0 && (
          <span className="text-ink-600 text-xs">↺ remise après chaque tirage</span>
        )}
      </div>
    </div>
  );
}

const TREE_W = 320;
const ROW_H = 28;
/** largeur moyenne d'un chiffre à 12 px */
const DIGIT_W = 7.4;

/**
 * Arbre pondéré : racine à gauche, un niveau par tirage, chemins de E en gras,
 * étiquettes posées sur leur branche ; un cercle suit le tirage en cours.
 */
function TreeFigure({
  paths,
  D,
  k,
  highlighted,
  drawn,
}: {
  paths: UrnPath[];
  D: number;
  k: number;
  highlighted: Set<string>;
  drawn: readonly number[];
}) {
  const H = paths.length * ROW_H + 8;
  // la colonne des feuilles s'élargit avec les fractions (« 120/1320 »)
  const longest = Math.max(...paths.map((q) => `${q.num}/${D}`.length));
  const leafW = Math.ceil(DIGIT_W * longest) + 16;
  const colW = (TREE_W - 16 - leafW) / k;
  const xOf = (depth: number) => 10 + depth * colW;
  const leafY = (i: number) => 4 + ROW_H * (i + 0.5);
  // chaque nœud (préfixe de chemin) au milieu de sa première et de sa dernière feuille
  const nodes = new Map<string, { y: number; first: number; color: number; depth: number }>();
  paths.forEach((q, i) => {
    for (let d = 1; d <= k; d++) {
      const key = pathName(q.colors.slice(0, d));
      const node = nodes.get(key);
      if (!node) nodes.set(key, { y: leafY(i), first: i, color: q.colors[d - 1]!, depth: d });
      else node.y = (leafY(node.first) + leafY(i)) / 2;
    }
  });
  const rootY = (leafY(0) + leafY(paths.length - 1)) / 2;
  const onPath = (prefix: string) => [...highlighted].some((h) => h.startsWith(prefix));
  const drawnName = pathName(drawn);
  const drawnDone = drawn.length === k;
  const edges: ReactNode[] = [];
  const labels: ReactNode[] = [];
  const seen = new Set<string>();
  paths.forEach((q) =>
    q.branches.forEach((b, d) => {
      const key = pathName(q.colors.slice(0, d + 1));
      if (seen.has(key)) return;
      seen.add(key);
      const parent = d === 0 ? { y: rootY } : nodes.get(pathName(q.colors.slice(0, d)))!;
      const child = nodes.get(key)!;
      const x1 = xOf(d) + (d === 0 ? 4 : 10);
      const x2 = xOf(d + 1) - 10;
      const hot = onPath(key);
      edges.push(
        <line
          key={key}
          x1={x1}
          y1={parent.y}
          x2={x2}
          y2={child.y}
          stroke={hot ? HIGHLIGHT : MUTED}
          strokeWidth={hot ? 3 : 1.5}
        />,
      );
      // l'étiquette posée SUR sa branche (halo), assez près du nœud d'arrivée — les
      // branches s'y écartent — sans toucher sa boule
      const t = Math.min(0.72, 1 - 24 / (x2 - x1));
      labels.push(
        <HaloText
          key={`l${key}`}
          x={x1 + (x2 - x1) * t}
          y={parent.y + (child.y - parent.y) * t + 4}
          textAnchor="middle"
          fontSize={12}
          fontWeight={hot ? 700 : 400}
          fill={hot ? HIGHLIGHT_TEXT : 'var(--color-ink-600)'}
        >
          {b.num}/{b.den}
        </HaloText>,
      );
    }),
  );
  return (
    <div className="mx-auto w-full max-w-[26rem]">
      <svg
        viewBox={`0 0 ${TREE_W} ${H}`}
        className={cn('border-ink-100 block w-full rounded-2xl border', FIGURE_BG_CLASS)}
        role="img"
        aria-label={`Arbre pondéré : ${paths.length} chemins, dénominateur commun ${D}. Chemins de l’événement : ${[...highlighted].join(', ') || 'aucun'}.`}
        data-testid="urn-tree"
      >
        {edges}
        <circle cx={xOf(0)} cy={rootY} r={4} fill="var(--color-ink-700)" />
        {[...nodes.entries()].map(([key, node]) => {
          const col = COLORS[node.color]!;
          const x = xOf(node.depth);
          const here = drawn.length >= node.depth && drawnName.slice(0, node.depth) === key;
          return (
            <g key={`n${key}`}>
              {here && (
                <circle
                  cx={x}
                  cy={node.y}
                  r={13}
                  fill="none"
                  stroke={DRAWN}
                  strokeWidth={2.5}
                  data-testid="urn-here"
                />
              )}
              <circle cx={x} cy={node.y} r={9} fill={col.fill} />
              <text
                x={x}
                y={node.y + 4}
                textAnchor="middle"
                fontSize={12}
                fontWeight={700}
                fill={col.ink}
              >
                {col.id}
              </text>
            </g>
          );
        })}
        {labels}
        {paths.map((q, i) => {
          const name = pathName(q.colors);
          const hot = highlighted.has(name);
          const x = xOf(k) + 14;
          return (
            <g key={`f${name}`}>
              {drawnDone && drawnName === name && (
                <rect
                  x={x - 4}
                  y={leafY(i) - 11}
                  width={leafW - 8}
                  height={22}
                  rx={6}
                  fill="none"
                  stroke={DRAWN}
                  strokeWidth={2}
                />
              )}
              <text
                x={x}
                y={leafY(i) + 4}
                fontSize={12}
                fontWeight={hot ? 700 : 400}
                fill={hot ? HIGHLIGHT_TEXT : 'var(--color-ink-500)'}
              >
                {q.num}/{D}
              </text>
            </g>
          );
        })}
      </svg>
      <ul className="sr-only">
        {paths.map((q) => (
          <li key={pathName(q.colors)}>
            {spoken(q.colors)} : {q.branches.map((b) => `${b.num} sur ${b.den}`).join(' fois ')},
            soit {q.num} sur {D}
            {highlighted.has(pathName(q.colors)) ? ', réalise l’événement' : ''}.
          </li>
        ))}
      </ul>
      {drawn.length > 0 && (
        <p className="text-ink-600 mt-1 text-center text-xs">
          {drawnDone
            ? `Encadré : le chemin de ton tirage (${drawnName}).`
            : 'Cerclé : où ton tirage en est dans l’arbre.'}
        </p>
      )}
    </div>
  );
}

/**
 * 1re → prépa : une probabilité conditionnelle lue sur le 2e niveau, comparée
 * à P(B₂) — l'indépendance à la mode de 1re : P_{R₁}(B₂) = P(B₂) ?
 */
function ConditionalLine({
  paths,
  counts,
  prepa,
}: {
  paths: UrnPath[];
  counts: readonly number[];
  prepa: boolean;
}) {
  const q = paths.find((x) => x.colors[0] !== x.colors[1]) ?? paths[0];
  if (!q || q.branches.length < 2) return null;
  const a = q.colors[0]!;
  const b2 = q.colors[1]!;
  const first = `${COLORS[a]!.id}_1`;
  const second = `${COLORS[b2]!.id}_2`;
  const cond = fraction(q.branches[1]!.num, q.branches[1]!.den);
  const condRaw = frac(q.branches[1]!.num, q.branches[1]!.den);
  // P(B₂) : sans information, la 2e boule a la même loi que la 1re
  const marginal = fraction(counts[b2]!, total(counts));
  const same = Math.abs(cond.value - marginal.value) < 1e-12;
  const lhs = prepa ? `P(${second} \\mid ${first})` : `P_{${first}}(${second})`;
  const condTex = cond.tex === condRaw ? condRaw : `${condRaw} = ${cond.tex}`;
  return (
    <section
      className="border-ink-100 space-y-1 rounded-2xl border px-3 py-3"
      data-testid="urn-conditional"
    >
      <MathText className="text-ink-900 block overflow-x-auto">
        {`$${lhs} = ${condTex} ${same ? '=' : '\\neq'} P(${second}) = ${marginal.tex}$`}
      </MathText>
      <p className="text-ink-600 text-sm">
        Le 2e niveau de l’arbre porte des probabilités conditionnelles : on sait déjà que la 1re
        boule est {COLORS[a]!.name} ({COLORS[a]!.id}₁).{' '}
        {same
          ? `Savoir ${COLORS[a]!.id}₁ ne change pas la probabilité de ${COLORS[b2]!.id}₂ : ces événements sont indépendants.`
          : `Savoir ${COLORS[a]!.id}₁ change la probabilité de ${COLORS[b2]!.id}₂ : ces événements ne sont pas indépendants.`}
      </p>
    </section>
  );
}

/**
 * Loi de X = nombre de boules de la couleur suivie. 1re : lue sur l'arbre,
 * E(X) par la définition. Tle : dénombrement des tirages ordonnés,
 * binomiale (avec remise) ou combinaisons (sans), E(X) = k·r/N, V(X) de la
 * binomiale. Prépa : A_N^k, loi hypergéométrique, variance sans remise.
 */
function LawPanel({
  setup,
  c,
  favorable,
  mode,
  onSend,
  sendCost,
}: {
  setup: UrnSetup;
  c: number;
  favorable: number;
  mode: Mode;
  onSend?: ToolProps['onSend'];
  sendCost: number;
}) {
  const { k, replace } = setup;
  const prepa = mode === 'prepa';
  const advanced = mode === 'terminale' || prepa;
  const n = total(setup.counts);
  const r = setup.counts[c]!;
  const col = COLORS[c]!;
  const D = denominator(setup);
  const treeLaw = lawFromPaths(setup, c);
  const law = advanced ? countLaw(setup, c) : treeLaw;
  const e = expectation(setup, c);
  const E = fraction(e.num, e.den);
  const v = variance(setup, c);
  const V = fraction(v.num, v.den);
  const pr = fraction(r, n);
  const ordered = Array.from({ length: k }, (_, i) => n - i).join(' \\times ');
  const countTex = replace
    ? `${n}^{${k}} = ${D}`
    : prepa
      ? `A_{${n}}^{${k}} = \\frac{${n}!}{${n - k}!} = ${D}`
      : `${ordered} = ${D}`;
  const factorial = Array.from({ length: k }, (_, i) => i + 1).reduce((s, x) => s * x, 1);
  const maxP = Math.max(...law.num) || 1;
  const sumJ = treeLaw.num.reduce((s, m, j) => s + j * m, 0);
  const defTerms = treeLaw.num.map((m, j) => `${j} \\times ${frac(m, D)}`);
  return (
    <section
      className="border-ink-100 space-y-3 rounded-2xl border px-3 py-3"
      aria-label="Loi du nombre de boules"
      data-testid="urn-advanced"
    >
      {advanced && (
        <div className="space-y-1">
          <MathText className="text-ink-900 block overflow-x-auto">{`$${countTex}$`}</MathText>
          <p className="text-ink-600 text-sm">
            Boules numérotées de 1 à {n} : {count(D)} tirages ordonnés possibles (
            {replace ? `${k}-uplets` : prepa ? 'arrangements' : `${k}-uplets d’éléments distincts`}
            ), tous équiprobables. {count(favorable)} réalisent E : P(E) = {favorable}/{D}.
          </p>
        </div>
      )}

      <div className="space-y-2">
        <p className="text-ink-900 text-sm font-semibold">
          X = nombre de boules {col.plural} parmi les {k}
        </p>
        <table className="text-ink-900 w-full text-sm tabular-nums" data-testid="urn-law">
          <tbody>
            {law.num.map((m, j) => (
              <tr key={j}>
                <td className="w-16 py-0.5">X = {j}</td>
                <td className="py-0.5">
                  <span
                    className="bg-primary-500 block h-3 rounded"
                    style={{ width: m === 0 ? 0 : `${Math.max(2, (m / maxP) * 100)}%` }}
                    aria-hidden
                  />
                </td>
                <td className="w-16 py-0.5 text-right">{fraction(m, law.den).plain}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!advanced && (
          <p className="text-ink-600 text-sm">
            Chaque P(X = j) s’obtient en ajoutant les chemins de l’arbre qui ont j boules{' '}
            {col.plural}.
          </p>
        )}
        {advanced &&
          (replace ? (
            <MathText className="text-ink-700 block overflow-x-auto">
              {`$\\text{Avec remise : } X \\sim \\mathcal{B}\\left(${k}${prepa ? ',\\ ' : ' ;\\ '}${pr.tex}\\right)$`}
            </MathText>
          ) : (
            <div className="space-y-1">
              <MathText className="text-ink-700 block overflow-x-auto">
                {`$\\binom{${n}}{${k}} = \\frac{${D}}{${k}!} = ${D / factorial}$`}
              </MathText>
              <p className="text-ink-600 text-sm">
                Sans tenir compte de l’ordre : {D / factorial} tirages simultanés. Chacun correspond
                à {k}! = {factorial} tirages ordonnés, donc les probabilités sont les mêmes.
              </p>
              <MathText className="text-ink-700 block overflow-x-auto">
                {`$P(X = j) = \\frac{\\binom{${r}}{j}\\binom{${n - r}}{${k} - j}}{\\binom{${n}}{${k}}}$`}
              </MathText>
              <p className="text-ink-600 text-sm">
                {prepa
                  ? `Sans remise, X suit la loi hypergéométrique (hors programme, classique en exercice) : j boules parmi les ${col.plural}, ${k} − j parmi les autres.`
                  : r > 0 && r < n
                    ? 'Sans remise, les tirages ne sont pas indépendants : X ne suit pas une loi binomiale. On compte avec des combinaisons.'
                    : 'On compte avec des combinaisons : on choisit j boules parmi celles de la couleur, les autres parmi le reste.'}
              </p>
            </div>
          ))}
      </div>

      <div className="space-y-1">
        <MathText className="text-ink-900 block overflow-x-auto">
          {advanced
            ? `$E(X) = ${k} \\times ${pr.tex} = ${E.tex}$`
            : `$${lines(
                'E(X)',
                [
                  ...(k === 3
                    ? [
                        { op: '=' as const, body: defTerms.slice(0, 2).join(' + ') },
                        { op: '+' as const, body: defTerms.slice(2).join(' + ') },
                      ]
                    : [{ op: '=' as const, body: defTerms.join(' + ') }]),
                  {
                    op: '=' as const,
                    body: `${frac(sumJ, D)}${frac(sumJ, D) === E.tex ? '' : ` = ${E.tex}`}`,
                  },
                ],
                true,
              )}$`}
        </MathText>
        {onSend && (
          <div className="flex justify-end">
            <SendToSheetButton
              label={E.plain}
              cost={sendCost}
              onClick={() => onSend({ atoms: E.atoms })}
            />
          </div>
        )}
      </div>
      {advanced && (
        <p className="text-ink-600 text-sm">
          Même espérance avec ou sans remise : chaque boule tirée est {col.name} avec la probabilité{' '}
          {pr.plain}.
        </p>
      )}
      {advanced && replace && (
        <MathText className="text-ink-700 block overflow-x-auto">
          {`$V(X) = ${k} \\times ${pr.tex} \\times \\left(1 - ${pr.tex}\\right) = ${V.tex}$`}
        </MathText>
      )}
      {prepa && !replace && (
        <>
          <MathText className="text-ink-700 block overflow-x-auto">
            {`$V(X) = ${k} \\times ${pr.tex} \\times \\left(1 - ${pr.tex}\\right) \\times \\frac{${n} - ${k}}{${n} - 1} = ${V.tex}$`}
          </MathText>
          <p className="text-ink-600 text-sm">
            Celle de la loi binomiale, multipliée par (N − k)/(N − 1) &lt; 1 : sans remise, les
            résultats se répartissent moins.
          </p>
        </>
      )}
    </section>
  );
}
